import { useCallback, useRef } from "react";

/**
 * Reconciles live "fill as you speak" voice dictation with a text input's
 * value setter.
 *
 * Model (deliberately simple + idempotent):
 * - When a recording session starts (`onStart`), snapshot the current input
 *   value as the immutable `base` for this session.
 * - On every transcript update (`onTranscript`), set the input to
 *   `base + separator + fullTranscript`. Because we always rebuild from the
 *   base, this is idempotent: repeated calls, growing text, a trailing final
 *   that equals the last interim, or missing events all converge to the same
 *   correct value. There is no delta math and no second write path.
 * - When the session ends (`onEnd`), clear the base. The input already holds
 *   the final text from the last `onTranscript` — nothing else to commit.
 *
 * This eliminates the interim-vs-final duality and the ordering hazards that
 * came with it.
 *
 * Usage:
 *   const dictation = useVoiceDictation(setValue);
 *   <VoiceInputButton {...dictation} />   // spreads onStart/onEnd/onTranscript
 */
export interface VoiceDictationHandlers {
  onStart: () => void;
  onEnd: () => void;
  onTranscript: (sessionTranscript: string) => void;
}

export function useVoiceDictation(setValue: (updater: (prev: string) => string) => void): VoiceDictationHandlers {
  // The input value when the current session began. `null` = no active session.
  const baseRef = useRef<string | null>(null);

  const onStart = useCallback(() => {
    // Snapshot the base for this session. Defer the actual read to the first
    // setValue call so we capture the freshest value (including anything the
    // user typed right up to pressing the mic).
    baseRef.current = null;
  }, []);

  const onEnd = useCallback(() => {
    // The input already holds the final text; drop the base so the next
    // session re-snapshots from the (possibly edited) current value.
    baseRef.current = null;
  }, []);

  const onTranscript = useCallback(
    (transcript: string) => {
      setValue((prev) => {
        // First update of the session captures the base from the live value.
        if (baseRef.current === null) baseRef.current = prev;
        const base = baseRef.current;
        const text = transcript.trim();
        if (!text) return base;
        const sep = base && !base.endsWith(" ") ? " " : "";
        return `${base}${sep}${text}`;
      });
    },
    [setValue],
  );

  return { onStart, onEnd, onTranscript };
}
